import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import useFetchAllComparisonResearchQueries from "../../hooks/useFetchAllComparisonResearchQueries";
import { assignResearchQueryToRule } from "../../services/comparisonResearchQueries";
import { getResearchQueryDetail } from "../../services/rulesService";
import { IComparisonResearchQuery } from "../../types/ResearchQueries";

interface AssignResearchQueryDialogProps {
  open: boolean;
  onClose: () => void;
  ruleData: {
    id: number;
    name: string;
  } | null;
  showToast: (message: string, type: "success" | "error" | "warning") => void;
  onSuccess?: () => void;
}

const AssignResearchQueryDialog: React.FC<AssignResearchQueryDialogProps> = ({
  open,
  onClose,
  ruleData,
  showToast,
  onSuccess,
}) => {
  const [selectedQuery, setSelectedQuery] =
    useState<IComparisonResearchQuery | null>(null);
  const [originalQuery, setOriginalQuery] =
    useState<IComparisonResearchQuery | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingAssigned, setIsFetchingAssigned] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const { allComparisonResearchQueries, isLoadingQueries, refetchQueries } =
    useFetchAllComparisonResearchQueries({ fetchOnMount: false });

  // Fetch queries and check for existing assignment when dialog opens
  useEffect(() => {
    if (open && ruleData?.id) {
      refetchQueries();
      fetchExistingAssignment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ruleData?.id]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedQuery(null);
      setOriginalQuery(null);
      setShowWarning(false);
    }
  }, [open]);

  const fetchExistingAssignment = async () => {
    if (!ruleData?.id) return;
    setIsFetchingAssigned(true);
    try {
      const result = await getResearchQueryDetail(ruleData.id);
      if (result && result.id) {
        const existingQuery: IComparisonResearchQuery = {
          id: result.id,
          name: result.name,
          code: result.code,
          description: result.description,
          rule_id: ruleData.id,
        };
        setOriginalQuery(existingQuery);
        setSelectedQuery(existingQuery);
      }
    } catch (error) {
      // No existing assignment - this is fine
      console.log("No existing research query assignment found");
    } finally {
      setIsFetchingAssigned(false);
    }
  };

  const handleQueryChange = (
    _event: React.SyntheticEvent,
    value: IComparisonResearchQuery | null
  ) => {
    setSelectedQuery(value);

    // Show warning if there's an existing assignment and user is changing it
    // Case 1: User selects a DIFFERENT query than the original
    // Case 2: User clears the selection (unassigning)
    if (originalQuery) {
      const isChangingToAnotherQuery = value && value.id !== originalQuery.id;
      const isClearingSelection = value === null;

      if (isChangingToAnotherQuery || isClearingSelection) {
        setShowWarning(true);
      } else {
        // User selected the same query as original (no change)
        setShowWarning(false);
      }
    } else {
      // No original assignment, no warning needed
      setShowWarning(false);
    }
  };

  const handleClose = (
    _event?: {},
    reason?: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose();
  };

  const handleSave = async () => {
    if (!ruleData?.id) {
      showToast("Rule information is missing", "error");
      return;
    }

    // Determine if this is an assign or unassign operation
    const isAssigning = selectedQuery !== null;

    // For unassign, we need the original query ID
    if (!isAssigning && !originalQuery?.id) {
      showToast("No research query to unassign", "error");
      return;
    }

    setIsSaving(true);
    try {
      await assignResearchQueryToRule({
        comparison_research_query_id: isAssigning
          ? selectedQuery.id
          : originalQuery!.id,
        assign: isAssigning,
        rule_id: isAssigning ? ruleData.id : null,
      });
      showToast(
        isAssigning
          ? "Research query assigned successfully"
          : "Research query unassigned successfully",
        "success"
      );
      onSuccess?.();
      onClose();
    } catch (error: any) {
      showToast(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to assign research query",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const ruleDisplayName = ruleData ? `(${ruleData.id}) ${ruleData.name}` : "";
  const isLoading = isLoadingQueries || isFetchingAssigned;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      className="modal-dialog-1"
    >
      <DialogTitle className="dialog-title" component="span">
        <span>Assign/UnAssign Research Query</span>
        <button className="close-icon" onClick={() => onClose()}></button>
      </DialogTitle>
      <DialogContent className="dialog-content">
        <Box sx={{ pt: 1 }}>
          <Grid container rowSpacing={3} columnSpacing={3}>
            <Grid item xs={12}>
              <TextField
                type="text"
                name="ruleName"
                value={ruleDisplayName}
                fullWidth
                variant="outlined"
                label="Rule Name"
                className="form-control-autocomplete"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={allComparisonResearchQueries}
                getOptionLabel={(option) =>
                  option ? `(${option.id}) ${option.code}` : ""
                }
                value={selectedQuery}
                onChange={handleQueryChange}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                loading={isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Research Query"
                    variant="outlined"
                    className="form-control-autocomplete"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Select a research query..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {showWarning && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <strong>Warning:</strong>{" "}
                  {selectedQuery === null
                    ? "Unassigning the research query cannot be rolled back. The current assignment will be permanently removed."
                    : "Changing the assigned research query cannot be rolled back. The previous assignment will be permanently replaced."}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions className="dialog-footer">
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={() => onClose()}
          className="btn-orange btn-border"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={handleSave}
          className="btn-orange"
          disabled={isSaving || isLoading}
        >
          {isSaving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <>
              <SaveOutlinedIcon /> &nbsp; Save
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignResearchQueryDialog;
